import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { supplierFeeApi } from '@/features/partner/supplierFee/riderToPartner/api/SupplierFeeApi';
import { RiderDto } from '@/features/partner/supplierFee/riderToPartner/types/supplierFee.types';
import { MissionDto, promoMissionApi } from '@/features/settlement/promotion/partnerToRider/api/PromoMissionApi';
import {
    promoSuppSettlementApi,
    PromoSuppSettlementDto,
} from '@/features/settlement/promotion/partnerToRider/api/PromoSuppSettlementApi';
import { getItem, STORAGE_KEYS } from '@/shared/utils/storage';
import { PartnerPortalLoginResponse } from '@/features/auth/types/auth.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import './PromotionSupplierToRiderPage.css';

// ─── 타입 정의 ────────────────────────────────────────────────────────────────

type MissionType = 'OVERAGE' | 'FIXED' | 'FULL_UNIT';
type PeriodType = 'DAILY' | 'WEEKLY';
type TargetType = 'ALL' | 'SPECIFIC';
type MissionStatus = 'ACTIVE' | 'INACTIVE';

// TODO: 백엔드에 MISSION_TYPE 공통코드 그룹 등록 필요
// comCode=OVERAGE|FIXED|FULL_UNIT, comCdName=표시명, val1=설명, val2=보상 라벨, val3=보상 단위
interface MissionTypeCode {
    code: MissionType;
    name: string;
    desc: string;
    rewardLabel: string;
    rewardUnit: string;
}

// TODO: 백엔드에 MISSION_PERIOD 공통코드 그룹 등록 필요
// comCode=DAILY|WEEKLY, comCdName=하루|일주일
interface PeriodCode {
    code: PeriodType;
    name: string;
}

interface MissionActualResult {
    totalAchievers: number; // 전체 달성 인원
    totalPayout: number; // 총 실지급액
    periodAchievers: number; // 주기별 평균 달성 인원
    periodPayout: number; // 주기별 평균 지급액
}

interface Mission {
    id: number;
    seqNo: number;
    title: string;
    missionType: MissionType;
    period: PeriodType;
    targetCount: number;
    rewardAmount: number;
    maxCount: number;
    startDate: string;
    endDate: string;
    description: string;
    targetType: TargetType;
    targetRiders: string[];
    status: MissionStatus;
    actualResult?: MissionActualResult;
}

interface FormState {
    missionType: MissionType;
    title: string;
    period: PeriodType;
    targetCount: string;
    rewardAmount: string;
    maxCount: string;
    startDate: string;
    endDate: string;
    description: string;
    targetType: TargetType;
    targetRiders: string[];
}

// ─── 공통코드 Fallback ────────────────────────────────────────────────────────

const FALLBACK_MISSION_TYPE_CODES: MissionTypeCode[] = [
    {
        code: 'OVERAGE',
        name: '초과분 단가 지급',
        desc: '기간 동안 X건 이상 수행하면 X건을 초과한 1건당 Y원 지급',
        rewardLabel: '초과 1건당 지급',
        rewardUnit: '원/초과 1건',
    },
    {
        code: 'FIXED',
        name: '목표 달성 고정',
        desc: '기간 동안 X건 이상 수행하면 Y원 일시 지급',
        rewardLabel: '목표 달성 지급액',
        rewardUnit: '원',
    },
    {
        code: 'FULL_UNIT',
        name: '목표 달성 전체단가',
        desc: '기간 동안 X건 이상 달성 시, 그 기간 전체 건수 × Z원 지급',
        rewardLabel: '건당 단가',
        rewardUnit: '원/건',
    },
];

const FALLBACK_PERIOD_CODES: PeriodCode[] = [
    { code: 'DAILY', name: '하루' },
    { code: 'WEEKLY', name: '일주일' },
];

const STATUS_LABEL: Record<MissionStatus, string> = {
    ACTIVE: '활성',
    INACTIVE: '비활성',
};

// ─── API 응답 → Mission 변환 ──────────────────────────────────────────────────

function toMission(dto: MissionDto): Mission {
    return {
        id: dto.seqNo,
        seqNo: dto.seqNo,
        title: dto.title,
        missionType: dto.missionType as MissionType,
        period: dto.periodType as PeriodType,
        targetCount: dto.targetCnt,
        rewardAmount: dto.rewardAmt,
        maxCount: dto.maxCnt,
        startDate: dto.startDate,
        endDate: dto.endDate,
        description: dto.description ?? '',
        targetType: dto.targetType as TargetType,
        targetRiders: dto.targetRiders ?? [],
        status: dto.statusCode as MissionStatus,
        actualResult: dto.actualResult
            ? {
                totalAchievers: dto.actualResult.totalAchievers,
                totalPayout: dto.actualResult.totalPayout,
                periodAchievers: dto.actualResult.periodAchievers,
                periodPayout: dto.actualResult.periodPayout,
            }
            : undefined,
    };
}

// ─── 유틸 ─────────────────────────────────────────────────────────────────────

// LocalDate가 배열 [2026, 6, 17] 또는 문자열 "2026-06-17"로 올 수 있어 ISO 문자열로 정규화
function toIsoDate(val: unknown): string {
    if (Array.isArray(val)) {
        const [y, m, d] = val as number[];
        return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    return String(val).substring(0, 10);
}

function formatNumber(value: string | number): string {
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    if (isNaN(num)) return '';
    return num.toLocaleString('ko-KR');
}

function calcDays(start: string, end: string): number {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    const diff = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
}

function calcOpportunities(days: number, period: PeriodType): number {
    if (days <= 0) return 0;
    return period === 'DAILY' ? days : Math.ceil(days / 7);
}

function getMissionMeta(code: MissionType, types: MissionTypeCode[]): MissionTypeCode {
    return types.find((t) => t.code === code) ?? FALLBACK_MISSION_TYPE_CODES.find((t) => t.code === code)!;
}

function getPeriodName(code: PeriodType, periods: PeriodCode[]): string {
    return periods.find((p) => p.code === code)?.name ?? code;
}

// ─── 미리보기 텍스트 생성 ──────────────────────────────────────────────────────

function buildPreviewMission(form: FormState, types: MissionTypeCode[], periods: PeriodCode[]): string {
    const reward = formatNumber(form.rewardAmount) || 'Y';
    const target = form.targetCount || 'X';
    const max = form.maxCount && form.maxCount !== '0' ? `최대 ${formatNumber(form.maxCount)}건` : '무제한';
    const periodName = getPeriodName(form.period, periods);

    if (form.missionType === 'OVERAGE') {
        return `${periodName} ${target}건 이상 수행 시 초과 1건당 ${reward}원 지급 (${max})`;
    }
    if (form.missionType === 'FIXED') {
        return `${periodName} ${target}건 이상 수행 시 ${reward}원 일시 지급`;
    }
    return `${periodName} ${target}건 이상 달성 시 전체 건수 × ${reward}원 지급 (${max})`;
}

function buildPreviewSchedule(form: FormState, periods: PeriodCode[]): string {
    const start = form.startDate || '시작일';
    const end = form.endDate || '종료일';
    const days = calcDays(form.startDate, form.endDate);
    const opp = calcOpportunities(days, form.period);
    const periodName = getPeriodName(form.period, periods);
    const dayStr = days > 0 ? ` (총 ${days}일)` : '';
    const oppStr = opp > 0 ? `, 총 ${opp}회 기회` : '';
    return `${start} ~ ${end}${dayStr} → ${periodName} 평가${oppStr}`;
}

function buildPreviewReward(form: FormState): string {
    const reward = formatNumber(form.rewardAmount) || 'Y';
    const target = form.targetCount || 'X';
    if (form.missionType === 'OVERAGE') return `${target}건 초과 시 초과 1건당 ${reward}원`;
    if (form.missionType === 'FIXED') return `${target}건 달성 시 ${reward}원 일시 지급`;
    return `${target}건 달성 시 전체 건수 × ${reward}원`;
}

// ─── 종료 미션 분석 ───────────────────────────────────────────────────────────

interface EndedMissionAnalysis {
    isOneDay: boolean;
    totalTargets: string;
    totalPayout: string;
    dailyTargets: string;
    dailyPayout: string;
}

function buildEndedMissionAnalysis(m: Mission): EndedMissionAnalysis {
    const isOneDay = m.startDate === m.endDate;
    const r = m.actualResult;

    if (!r) {
        return {
            isOneDay,
            totalTargets: '미집계',
            totalPayout: '미집계',
            dailyTargets: '미집계',
            dailyPayout: '미집계',
        };
    }

    return {
        isOneDay,
        totalTargets: `${formatNumber(r.totalAchievers)}명`,
        totalPayout: `${formatNumber(r.totalPayout)}원`,
        dailyTargets: `${formatNumber(r.periodAchievers)}명`,
        dailyPayout: `${formatNumber(r.periodPayout)}원`,
    };
}

// ─── 컴포넌트 ─────────────────────────────────────────────────────────────────

const DEFAULT_FORM: FormState = {
    missionType: 'OVERAGE',
    title: '',
    period: 'DAILY',
    targetCount: '',
    rewardAmount: '',
    maxCount: '',
    startDate: '',
    endDate: '',
    description: '',
    targetType: 'ALL',
    targetRiders: [],
};

const PromotionSupplierToRiderPage: React.FC = () => {
    const { showToast } = useToast();

    // 페이지 진입 시 1회만 읽음 - 렌더마다 새 참조가 생기는 훅 함수 대신 getItem 직접 사용
    const isReadOnly = useMemo(() => getItem<string>('USER_TYPE') !== 'SUPPLIER', []);
    // 백엔드 로그인 응답에 suppId가 없음 — 협력사 계정은 userId === suppId
    const suppId = useMemo(() => {
        const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
        return userInfo?.suppId ?? userInfo?.userId ?? null;
    }, []);

    const today = useMemo(() => new Date().toISOString().split('T')[0], []);

    const [form, setForm] = useState<FormState>(DEFAULT_FORM);
    const [missions, setMissions] = useState<Mission[]>([]);
    const [missionsLoading, setMissionsLoading] = useState(false);
    const [editingSeqNo, setEditingSeqNo] = useState<number | null>(null);
    const [riderSearch, setRiderSearch] = useState('');

    // 행 클릭 시 열리는 상세 모달
    const [selectedMission, setSelectedMission] = useState<Mission | null>(null);

    // 라이더 목록 (API 연동) - "특정 라이더만" 선택 시 1회 지연 로딩
    const [riders, setRiders] = useState<RiderDto[]>([]);
    const [ridersLoading, setRidersLoading] = useState(false);
    const [ridersLoaded, setRidersLoaded] = useState(false);

    // 보류 현황 (본사 계정 전용)
    const [settlementStartDate, setSettlementStartDate] = useState('');
    const [settlementEndDate, setSettlementEndDate] = useState('');
    const [settlements, setSettlements] = useState<PromoSuppSettlementDto[]>([]);
    const [settlementsLoading, setSettlementsLoading] = useState(false);

    // 공통코드 (fallback으로 초기화, API 성공 시 교체)
    const [missionTypeCodes, setMissionTypeCodes] = useState<MissionTypeCode[]>(FALLBACK_MISSION_TYPE_CODES);
    const [periodCodes, setPeriodCodes] = useState<PeriodCode[]>(FALLBACK_PERIOD_CODES);

    // TODO: 백엔드에 MISSION_TYPE, MISSION_PERIOD 공통코드 그룹 등록 후 정상 응답
    const fetchCommonCodes = useCallback(async () => {
        try {
            const res = await commonCodeApi.getCommonCodeList('MISSION_TYPE');
            const valid = res.codes.filter((c) => c.useYn === 'Y');
            if (valid.length > 0) {
                setMissionTypeCodes(
                    valid.map((c) => ({
                        code: c.comCode as MissionType,
                        name: c.comCdName,
                        desc: c.val1 || '',
                        rewardLabel: c.val2 || '',
                        rewardUnit: c.val3 || '',
                    }))
                );
            }
        } catch {
            // 공통코드 미등록 시 fallback 유지
        }

        try {
            const res = await commonCodeApi.getCommonCodeList('MISSION_PERIOD');
            const valid = res.codes.filter((c) => c.useYn === 'Y');
            if (valid.length > 0) {
                setPeriodCodes(valid.map((c) => ({ code: c.comCode as PeriodType, name: c.comCdName })));
            }
        } catch {
            // 공통코드 미등록 시 fallback 유지
        }
    }, []);

    // 협력사 소속 계약중 라이더 목록 조회 (GET /api/v1/rider/list?suppId=&statusCode=UNDER_CONTRACT)
    // "특정 라이더만" 선택 시 1회만 호출 (마운트 시 호출하지 않음)
    const fetchRiders = useCallback(async () => {
        if (!suppId) {
            showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }
        try {
            setRidersLoading(true);
            const res = await supplierFeeApi.getRiderList(suppId);
            setRiders(res.riders || []);
            setRidersLoaded(true);
        } catch {
            showToast('라이더 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setRidersLoading(false);
        }
    }, [suppId, showToast]);

    const fetchMissions = useCallback(async () => {
        if (!isReadOnly && !suppId) return;
        try {
            setMissionsLoading(true);
            const res = await promoMissionApi.getMissionList(isReadOnly ? undefined : suppId ?? undefined);
            setMissions(res.missions.map(toMission));
        } catch {
            showToast('미션 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setMissionsLoading(false);
        }
    }, [suppId, isReadOnly, showToast]);

    const fetchSettlements = useCallback(async () => {
        if (!settlementStartDate || !settlementEndDate) {
            showToast('정산 기간을 입력해 주세요.', { type: ToastType.ERROR });
            return;
        }
        try {
            setSettlementsLoading(true);
            const data = await promoSuppSettlementApi.getList(settlementStartDate, settlementEndDate);
            setSettlements(data.filter((s) => s.holdStatus === 'PENDING_DEPOSIT'));
        } catch {
            showToast('보류 현황을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSettlementsLoading(false);
        }
    }, [settlementStartDate, settlementEndDate, showToast]);

    async function handleForceProcess(s: PromoSuppSettlementDto) {
        if (!window.confirm(`[${s.suppId}] 협력사를 강제 지급 처리하시겠습니까?\n잔액이 마이너스가 될 수 있습니다.`))
            return;
        try {
            const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
            await promoSuppSettlementApi.forceProcess(
                s.suppId,
                toIsoDate(s.acctStDate),
                toIsoDate(s.acctEdDate),
                userInfo?.userId ?? 'UNKNOWN'
            );
            showToast('강제 지급 처리가 완료되었습니다.', { type: ToastType.SUCCESS });
            await fetchSettlements();
        } catch {
            showToast('강제 지급 처리에 실패했습니다.', { type: ToastType.ERROR });
        }
    }

    async function handleSwOnly(s: PromoSuppSettlementDto) {
        if (
            !window.confirm(
                `[${s.suppId}] 협력사의 주정산만 진행하시겠습니까?\n프로모션 차감(PD)은 이후 입금 또는 강제 지급 시 처리됩니다.`
            )
        )
            return;
        try {
            const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
            await promoSuppSettlementApi.processSwOnly(
                s.suppId,
                toIsoDate(s.acctStDate),
                toIsoDate(s.acctEdDate),
                userInfo?.userId ?? 'UNKNOWN'
            );
            showToast('주정산 진행이 완료되었습니다.', { type: ToastType.SUCCESS });
            await fetchSettlements();
        } catch {
            showToast('주정산 진행에 실패했습니다.', { type: ToastType.ERROR });
        }
    }

    useEffect(() => {
        fetchCommonCodes();
        fetchMissions();
    }, [fetchCommonCodes, fetchMissions]);

    // 모달 열릴 때 스크롤 잠금
    useEffect(() => {
        if (selectedMission) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [selectedMission]);

    function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function handleMissionTypeChange(code: MissionType) {
        setForm((prev) => ({ ...prev, missionType: code, rewardAmount: '' }));
    }

    function toggleRider(riderId: string) {
        setForm((prev) => {
            const has = prev.targetRiders.includes(riderId);
            return {
                ...prev,
                targetRiders: has ? prev.targetRiders.filter((id) => id !== riderId) : [...prev.targetRiders, riderId],
            };
        });
    }

    const isValid = useMemo(() => {
        if (!form.title.trim()) return false;
        if (!form.targetCount || parseInt(form.targetCount) <= 0) return false;
        if (!form.rewardAmount || parseInt(form.rewardAmount) <= 0) return false;
        if (!form.startDate) return false;
        if (!form.endDate) return false;
        if (form.startDate > form.endDate) return false;
        if (form.targetType === 'SPECIFIC' && form.targetRiders.length === 0) return false;
        return true;
    }, [form]);

    async function handleRegister() {
        if (!isValid || isReadOnly || !suppId) return;

        // 1. 미션 제목 중복 검사
        const titleTrimmed = form.title.trim();
        if (missions.some((m) => m.title === titleTrimmed)) {
            showToast('이미 동일한 제목의 미션이 존재합니다.', { type: ToastType.ERROR });
            return;
        }

        try {
            const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
            await promoMissionApi.registerMission({
                suppId,
                title: titleTrimmed,
                missionType: form.missionType,
                periodType: form.period,
                targetCnt: parseInt(form.targetCount),
                rewardAmt: parseInt(form.rewardAmount),
                maxCnt: parseInt(form.maxCount) || 0,
                startDate: form.startDate,
                endDate: form.endDate,
                description: form.description.trim(),
                targetType: form.targetType,
                targetRiders: form.targetRiders,
                insUser: userInfo?.userId ?? 'UNKNOWN',
            });
            showToast('미션이 등록되었습니다.', { type: ToastType.SUCCESS });
            setForm(DEFAULT_FORM);
            setRiderSearch('');
            await fetchMissions();
        } catch {
            showToast('미션 등록에 실패했습니다.', { type: ToastType.ERROR });
        }
    }

    function handleReset() {
        setEditingSeqNo(null);
        setForm(DEFAULT_FORM);
        setRiderSearch('');
    }

    async function handleDelete(seqNo: number) {
        if (!window.confirm('미션을 삭제하시겠습니까?')) return;
        try {
            await promoMissionApi.deleteMission(seqNo);
            showToast('미션이 삭제되었습니다.', { type: ToastType.SUCCESS });
            setSelectedMission(null);
            await fetchMissions();
        } catch {
            showToast('미션 삭제에 실패했습니다.', { type: ToastType.ERROR });
        }
    }

    async function handleStatusToggle(m: Mission) {
        const newStatus: MissionStatus = m.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        const label = newStatus === 'ACTIVE' ? '활성화' : '비활성화';
        if (!window.confirm(`미션을 ${label}하시겠습니까?`)) return;
        try {
            const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
            await promoMissionApi.updateMissionStatus(m.seqNo, newStatus, userInfo?.userId ?? 'UNKNOWN');
            showToast(`미션이 ${label}되었습니다.`, { type: ToastType.SUCCESS });
            await fetchMissions();
        } catch {
            showToast(`미션 ${label}에 실패했습니다.`, { type: ToastType.ERROR });
        }
    }

    function handleEditStart(m: Mission) {
        setEditingSeqNo(m.seqNo);
        setForm({
            missionType: m.missionType,
            title: m.title,
            period: m.period,
            targetCount: String(m.targetCount),
            rewardAmount: String(m.rewardAmount),
            maxCount: String(m.maxCount),
            startDate: m.startDate,
            endDate: m.endDate,
            description: m.description,
            targetType: m.targetType,
            targetRiders: [...m.targetRiders],
        });
        setRiderSearch('');
        if (m.targetType === 'SPECIFIC' && !ridersLoaded) fetchRiders();
        setSelectedMission(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    async function handleUpdate() {
        if (!isValid || !editingSeqNo) return;
        const titleTrimmed = form.title.trim();
        if (missions.some((m) => m.seqNo !== editingSeqNo && m.title === titleTrimmed)) {
            showToast('이미 동일한 제목의 미션이 존재합니다.', { type: ToastType.ERROR });
            return;
        }
        try {
            const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
            await promoMissionApi.updateMission(editingSeqNo, {
                title: titleTrimmed,
                missionType: form.missionType,
                periodType: form.period,
                targetCnt: parseInt(form.targetCount),
                rewardAmt: parseInt(form.rewardAmount),
                maxCnt: parseInt(form.maxCount) || 0,
                startDate: form.startDate,
                endDate: form.endDate,
                description: form.description.trim(),
                targetType: form.targetType,
                targetRiders: form.targetRiders,
                updUser: userInfo?.userId ?? 'UNKNOWN',
            });
            showToast('미션이 수정되었습니다.', { type: ToastType.SUCCESS });
            setEditingSeqNo(null);
            setForm(DEFAULT_FORM);
            setRiderSearch('');
            await fetchMissions();
        } catch {
            showToast('미션 수정에 실패했습니다.', { type: ToastType.ERROR });
        }
    }

    // 종료 전 미션: 종료일이 오늘 이상 (ACTIVE + INACTIVE 포함, 비활성화 후 목록에서 사라지지 않도록)
    const activeMissions = useMemo(() => missions.filter((m) => m.endDate >= today), [missions, today]);

    const activeCount = useMemo(() => activeMissions.filter((m) => m.status === 'ACTIVE').length, [activeMissions]);

    // 종료된 미션: 종료일이 오늘 이전
    const endedMissions = useMemo(() => missions.filter((m) => m.endDate < today), [missions, today]);

    const filteredRiders = riders.filter((r) => r.riderName.includes(riderSearch) || r.riderId.includes(riderSearch));

    const currentMeta = getMissionMeta(form.missionType, missionTypeCodes);
    const previewMission = buildPreviewMission(form, missionTypeCodes, periodCodes);
    const previewSchedule = buildPreviewSchedule(form, periodCodes);
    const previewReward = buildPreviewReward(form);

    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">라이더 미션 설정 (협력사 → 라이더 프로모션)</h1>
                <p className="page-subtitle">
                    소속 라이더에게 인센티브 미션을 부여합니다. 기간(하루/일주일) 동안 목표 건수를 달성하면 보상이
                    지급됩니다.
                </p>
            </div>

            {/* 본사 계정 안내 배너 */}
            {isReadOnly && (
                <div className="mission-readonly-banner">
                    <i className="fas fa-info-circle" />
                    본사 계정으로는 미션 등록이 불가합니다. 미션 등록은 협력사 계정으로 로그인 후 이용하세요.
                </div>
            )}

            {/* ── 프로모션 보류 현황 (본사 계정 전용) ─────────────────────────── */}
            {isReadOnly && (
                <div className="filter-section mission-list-section">
                    <div className="mission-list-header">
                        <span className="mission-section-title">프로모션 보류 현황 관리</span>
                    </div>

                    {/* 기간 조회 */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                        <input
                            type="date"
                            className="mission-input"
                            style={{ width: 140 }}
                            value={settlementStartDate}
                            onChange={(e) => setSettlementStartDate(e.target.value)}
                        />
                        <span>~</span>
                        <input
                            type="date"
                            className="mission-input"
                            style={{ width: 140 }}
                            value={settlementEndDate}
                            onChange={(e) => setSettlementEndDate(e.target.value)}
                        />
                        <button
                            type="button"
                            className="mission-btn mission-btn-primary"
                            style={{ padding: '6px 16px' }}
                            onClick={fetchSettlements}
                            disabled={settlementsLoading}
                        >
                            {settlementsLoading ? '조회 중...' : '조회'}
                        </button>
                    </div>

                    {settlements.length === 0 ? (
                        <div className="mission-empty">
                            <i className="fas fa-check-circle" />
                            <p>기간을 선택 후 조회하거나, 처리 대기 중인 항목이 없습니다.</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="supplier-selection-table">
                                <thead>
                                <tr>
                                    <th>협력사 ID</th>
                                    <th>정산 기간</th>
                                    <th>프로모션 비용</th>
                                    <th>주정산 비용</th>
                                    <th>부족금액</th>
                                    <th>상태</th>
                                    <th>처리</th>
                                </tr>
                                </thead>
                                <tbody>
                                {settlements.map((s) => {
                                    const partnerAmt = s.partnerAcctVal ?? 0;
                                    const shortfall = s.totalPromoCost - partnerAmt;
                                    return (
                                        <tr key={s.seqNo}>
                                            <td>{s.suppId}</td>
                                            <td>{toIsoDate(s.acctStDate)} ~ {toIsoDate(s.acctEdDate)}</td>
                                            <td>{s.totalPromoCost.toLocaleString('ko-KR')}원</td>
                                            <td>{partnerAmt.toLocaleString('ko-KR')}원</td>
                                            <td style={{ color: '#e53e3e', fontWeight: 600 }}>
                                                {shortfall.toLocaleString('ko-KR')}원
                                            </td>
                                            <td>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '2px 8px',
                                                        borderRadius: 4,
                                                        fontSize: '0.8rem',
                                                        fontWeight: 600,
                                                        background: '#fed7d7',
                                                        color: '#c53030',
                                                    }}>
                                                        입금대기
                                                    </span>
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="mission-table-actions">
                                                    <button
                                                        type="button"
                                                        className="mission-action-btn mission-action-delete"
                                                        onClick={() => handleForceProcess(s)}
                                                    >
                                                        강제진행
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="mission-action-btn mission-action-activate"
                                                        onClick={() => handleSwOnly(s)}
                                                    >
                                                        주정산만 진행
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── 새 미션 등록 (협력사 계정만 노출) ──────────────────────────── */}
            {!isReadOnly && (
                <div className="filter-section mission-register-section">
                    <div className="mission-section-title">{editingSeqNo ? '미션 수정' : '새 미션 등록'}</div>

                    {/* 미션 유형 선택 — MISSION_TYPE 공통코드 */}
                    <div className="mission-type-row">
                        {missionTypeCodes.map((type) => (
                            <button
                                key={type.code}
                                type="button"
                                className={`mission-type-card${form.missionType === type.code ? ' selected' : ''}`}
                                onClick={() => handleMissionTypeChange(type.code)}
                            >
                                <span className="mission-type-label">{type.name}</span>
                                <span className="mission-type-desc">{type.desc}</span>
                            </button>
                        ))}
                    </div>

                    {/* 입력 폼 */}
                    <div className="mission-form-grid">
                        {/* 미션 제목 */}
                        <div className="mission-field full-width">
                            <label className="mission-label">
                                미션 제목 <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                className="mission-input"
                                placeholder="예) 5월 일일 50건 챌린지"
                                value={form.title}
                                onChange={(e) => setField('title', e.target.value)}
                                maxLength={100}
                            />
                        </div>

                        {/* 기간 — MISSION_PERIOD 공통코드 */}
                        <div className="mission-field">
                            <label className="mission-label">기간</label>
                            <select
                                className="mission-select"
                                value={form.period}
                                onChange={(e) => setField('period', e.target.value as PeriodType)}
                            >
                                {periodCodes.map((p) => (
                                    <option key={p.code} value={p.code}>
                                        {p.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* 목표 건수 */}
                        <div className="mission-field">
                            <label className="mission-label">
                                목표 건수 <span className="required">*</span>
                            </label>
                            <div className="mission-input-group">
                                <input
                                    type="number"
                                    className="mission-input"
                                    placeholder="0"
                                    min={1}
                                    value={form.targetCount}
                                    onChange={(e) => setField('targetCount', e.target.value)}
                                />
                                <span className="mission-unit">건</span>
                            </div>
                        </div>

                        {/* 보상 금액 — 라벨·단위는 MISSION_TYPE 공통코드 val2·val3 */}
                        <div className="mission-field">
                            <label className="mission-label">
                                {currentMeta.rewardLabel} <span className="required">*</span>
                            </label>
                            <div className="mission-input-group">
                                <input
                                    type="number"
                                    className="mission-input"
                                    placeholder="0"
                                    min={1}
                                    value={form.rewardAmount}
                                    onChange={(e) => setField('rewardAmount', e.target.value)}
                                />
                                <span className="mission-unit">{currentMeta.rewardUnit}</span>
                            </div>
                        </div>

                        {/* 최대 인정 건수 (FIXED 유형 제외) */}
                        {form.missionType !== 'FIXED' && (
                            <div className="mission-field">
                                <label className="mission-label">
                                    최대 인정 건수 <span className="mission-hint">(0=무제한)</span>
                                </label>
                                <div className="mission-input-group">
                                    <input
                                        type="number"
                                        className="mission-input"
                                        placeholder="0"
                                        min={0}
                                        value={form.maxCount}
                                        onChange={(e) => setField('maxCount', e.target.value)}
                                    />
                                    <span className="mission-unit">건</span>
                                </div>
                            </div>
                        )}

                        {/* 시작일 */}
                        <div className="mission-field">
                            <label className="mission-label">
                                시작일 <span className="required">*</span>
                            </label>
                            <input
                                type="date"
                                className="mission-input"
                                value={form.startDate}
                                onChange={(e) => setField('startDate', e.target.value)}
                            />
                        </div>

                        {/* 종료일 */}
                        <div className="mission-field">
                            <label className="mission-label">
                                종료일 <span className="required">*</span>
                            </label>
                            <input
                                type="date"
                                className="mission-input"
                                value={form.endDate}
                                min={form.startDate || undefined}
                                onChange={(e) => setField('endDate', e.target.value)}
                            />
                        </div>

                        {/* 설명 */}
                        <div className="mission-field full-width">
                            <label className="mission-label">
                                설명 <span className="mission-hint">(선택)</span>
                            </label>
                            <textarea
                                className="mission-textarea"
                                placeholder="라이더에게 안내할 추가 설명"
                                rows={2}
                                value={form.description}
                                onChange={(e) => setField('description', e.target.value)}
                                maxLength={300}
                            />
                        </div>
                    </div>

                    {/* 대상 라이더 선택 */}
                    <div className="mission-target-section">
                        <label className="mission-label">대상 라이더</label>
                        <div className="mission-radio-row">
                            <label className="mission-radio-label">
                                <input
                                    type="radio"
                                    value="ALL"
                                    checked={form.targetType === 'ALL'}
                                    onChange={() => setField('targetType', 'ALL')}
                                />
                                협력사 전체 라이더
                            </label>
                            <label className="mission-radio-label">
                                <input
                                    type="radio"
                                    value="SPECIFIC"
                                    checked={form.targetType === 'SPECIFIC'}
                                    onChange={() => {
                                        setField('targetType', 'SPECIFIC');
                                        if (!ridersLoaded) fetchRiders();
                                    }}
                                />
                                특정 라이더만
                            </label>
                        </div>

                        {form.targetType === 'SPECIFIC' && (
                            <div className="rider-selector">
                                <input
                                    type="text"
                                    className="mission-input rider-search-input"
                                    placeholder="라이더명 또는 ID 검색"
                                    value={riderSearch}
                                    onChange={(e) => setRiderSearch(e.target.value)}
                                />
                                <div className="rider-list">
                                    {ridersLoading ? (
                                        <span className="rider-empty">라이더 목록을 불러오는 중...</span>
                                    ) : filteredRiders.length > 0 ? (
                                        filteredRiders.map((rider) => (
                                            <label key={rider.riderId} className="rider-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={form.targetRiders.includes(rider.riderId)}
                                                    onChange={() => toggleRider(rider.riderId)}
                                                />
                                                {rider.riderName} ({rider.riderId})
                                            </label>
                                        ))
                                    ) : (
                                        <span className="rider-empty">
                                            {riderSearch ? '검색 결과가 없습니다.' : '소속 라이더가 없습니다.'}
                                        </span>
                                    )}
                                </div>
                                {form.targetRiders.length > 0 && (
                                    <div className="rider-selected-tags">
                                        {form.targetRiders.map((id) => {
                                            const r = riders.find((x) => x.riderId === id);
                                            return (
                                                <span key={id} className="rider-tag">
                                                    {r?.riderName ?? id}
                                                    <button type="button" onClick={() => toggleRider(id)}>
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* 미션 미리보기 */}
                    <div className="mission-preview">
                        <div className="mission-preview-title">미션 미리보기</div>
                        <div className="mission-preview-row">
                            <span className="mission-preview-key">미션</span>
                            <span className="mission-preview-value">{previewMission}</span>
                        </div>
                        <div className="mission-preview-row">
                            <span className="mission-preview-key">일정</span>
                            <span className="mission-preview-value">{previewSchedule}</span>
                        </div>
                        <div className="mission-preview-row">
                            <span className="mission-preview-key">보상</span>
                            <span className="mission-preview-value">{previewReward}</span>
                        </div>
                        <div className="mission-preview-row">
                            <span className="mission-preview-key">대상</span>
                            <span className="mission-preview-value">
                                {form.targetType === 'ALL'
                                    ? '협력사 전체 라이더'
                                    : form.targetRiders.length > 0
                                        ? `특정 라이더 ${form.targetRiders.length}명`
                                        : '특정 라이더 (미선택)'}
                            </span>
                        </div>
                    </div>

                    {/* 버튼 */}
                    <div className="mission-actions">
                        <button type="button" className="mission-btn mission-btn-secondary" onClick={handleReset}>
                            {editingSeqNo ? '수정 취소' : '초기화'}
                        </button>
                        <button
                            type="button"
                            className="mission-btn mission-btn-primary"
                            onClick={editingSeqNo ? handleUpdate : handleRegister}
                            disabled={!isValid}
                        >
                            {editingSeqNo ? '수정 완료' : '+ 미션 등록'}
                        </button>
                    </div>
                </div>
            )}

            {/* ── 활성 미션 목록 ────────────────────────────────────────────── */}
            <div className="filter-section mission-list-section">
                <div className="mission-list-header">
                    <span className="mission-section-title">
                        <span className="mission-active-dot">●</span> 활성 미션 ({activeCount}건
                        {activeMissions.length > activeCount
                            ? ` / 비활성 ${activeMissions.length - activeCount}건`
                            : ''}
                        )
                    </span>
                    <button type="button" className="mission-refresh-btn" onClick={fetchMissions}>
                        <i className="fas fa-sync-alt" /> 새로고침
                    </button>
                </div>

                {missionsLoading ? (
                    <div className="mission-empty">
                        <i className="fas fa-spinner fa-spin" />
                        <p>미션 목록을 불러오는 중...</p>
                    </div>
                ) : activeMissions.length === 0 ? (
                    <div className="mission-empty">
                        <i className="fas fa-inbox" />
                        <p>활성 미션이 없습니다.</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="supplier-selection-table">
                            <thead>
                            <tr>
                                <th>미션 제목</th>
                                <th>유형</th>
                                <th>기간</th>
                                <th>시작일</th>
                                <th>종료일</th>
                                <th>목표 건수</th>
                                <th>보상 조건</th>
                                <th>대상 라이더</th>
                                <th>상태</th>
                                {!isReadOnly && <th>관리</th>}
                            </tr>
                            </thead>
                            <tbody>
                            {activeMissions.map((m) => {
                                const typeMeta = getMissionMeta(m.missionType, missionTypeCodes);
                                const periodName = getPeriodName(m.period, periodCodes);
                                return (
                                    <tr
                                        key={m.id}
                                        className="mission-table-row-clickable"
                                        onClick={() => setSelectedMission(m)}
                                    >
                                        <td
                                            style={{
                                                textAlign: 'left',
                                                maxWidth: 200,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {m.title}
                                        </td>
                                        <td>
                                                <span
                                                    className={`mission-type-badge badge-${m.missionType.toLowerCase()}`}
                                                >
                                                    {typeMeta.name}
                                                </span>
                                        </td>
                                        <td>{periodName}</td>
                                        <td>{m.startDate}</td>
                                        <td>{m.endDate}</td>
                                        <td>{m.targetCount.toLocaleString()}건</td>
                                        <td>
                                            {formatNumber(m.rewardAmount)}
                                            {typeMeta.rewardUnit}
                                        </td>
                                        <td>{m.targetType === 'ALL' ? '전체' : `${m.targetRiders.length}명`}</td>
                                        <td>
                                                <span
                                                    className={`mission-status-badge status-${m.status.toLowerCase()}`}
                                                >
                                                    {STATUS_LABEL[m.status]}
                                                </span>
                                        </td>
                                        {!isReadOnly && (
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="mission-table-actions">
                                                    <button
                                                        type="button"
                                                        className="mission-action-btn mission-action-edit"
                                                        onClick={() => handleEditStart(m)}
                                                    >
                                                        수정
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`mission-action-btn ${m.status === 'ACTIVE' ? 'mission-action-deactivate' : 'mission-action-activate'}`}
                                                        onClick={() => handleStatusToggle(m)}
                                                    >
                                                        {m.status === 'ACTIVE' ? '비활성' : '활성화'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="mission-action-btn mission-action-delete"
                                                        onClick={() => handleDelete(m.seqNo)}
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── 종료된 미션 목록 ──────────────────────────────────────────── */}
            <div className="filter-section mission-list-section">
                <div className="mission-list-header">
                    <span className="mission-section-title">
                        <span className="mission-ended-dot">●</span> 종료된 미션 ({endedMissions.length}건)
                    </span>
                </div>

                {endedMissions.length === 0 ? (
                    <div className="mission-empty">
                        <i className="fas fa-history" />
                        <p>종료된 미션이 없습니다.</p>
                    </div>
                ) : (
                    <div className="ended-mission-list">
                        {endedMissions.map((m) => {
                            const typeMeta = getMissionMeta(m.missionType, missionTypeCodes);
                            const analysis = buildEndedMissionAnalysis(m);
                            return (
                                <div key={m.id} className="ended-mission-item" onClick={() => setSelectedMission(m)}>
                                    <div className="ended-mission-left">
                                        <span className={`mission-type-badge badge-${m.missionType.toLowerCase()}`}>
                                            {typeMeta.name}
                                        </span>
                                        <span className="ended-mission-title">{m.title}</span>
                                        <span className="ended-mission-date">
                                            {m.startDate} ~ {m.endDate}
                                        </span>
                                    </div>
                                    <div className="ended-mission-analysis">
                                        <span className="ended-analysis-chunk">
                                            총 대상자 - <strong>{analysis.totalTargets}</strong>
                                            {' / '}
                                            지급액 - <strong>{analysis.totalPayout}</strong>
                                        </span>
                                        {!analysis.isOneDay && (
                                            <>
                                                <span className="ended-analysis-sep">||</span>
                                                <span className="ended-analysis-chunk">
                                                    일 대상자 - <strong>{analysis.dailyTargets}</strong>
                                                    {' / '}일 평균 지급액 - <strong>{analysis.dailyPayout}</strong>
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ── 미션 상세 모달 ────────────────────────────────────────────── */}
            {selectedMission && (
                <div className="mission-modal-overlay" onClick={() => setSelectedMission(null)}>
                    <div className="mission-modal" onClick={(e) => e.stopPropagation()}>
                        {/* 헤더 */}
                        <div className="mission-modal-header">
                            <div className="mission-modal-title-row">
                                <span
                                    className={`mission-type-badge badge-${selectedMission.missionType.toLowerCase()}`}
                                >
                                    {getMissionMeta(selectedMission.missionType, missionTypeCodes).name}
                                </span>
                                <h3 className="mission-modal-title">{selectedMission.title}</h3>
                                <span className={`mission-status-badge status-${selectedMission.status.toLowerCase()}`}>
                                    {STATUS_LABEL[selectedMission.status]}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="mission-modal-close"
                                onClick={() => setSelectedMission(null)}
                            >
                                ×
                            </button>
                        </div>

                        {/* 본문 */}
                        <div className="mission-modal-body">
                            <div className="mission-modal-grid">
                                <div className="mission-modal-item">
                                    <span className="mission-modal-key">기간</span>
                                    <span className="mission-modal-value">
                                        {getPeriodName(selectedMission.period, periodCodes)}
                                    </span>
                                </div>
                                <div className="mission-modal-item">
                                    <span className="mission-modal-key">목표 건수</span>
                                    <span className="mission-modal-value">
                                        {selectedMission.targetCount.toLocaleString()}건
                                    </span>
                                </div>
                                <div className="mission-modal-item">
                                    <span className="mission-modal-key">보상</span>
                                    <span className="mission-modal-value">
                                        {formatNumber(selectedMission.rewardAmount)}
                                        {getMissionMeta(selectedMission.missionType, missionTypeCodes).rewardUnit}
                                    </span>
                                </div>
                                {selectedMission.missionType !== 'FIXED' && (
                                    <div className="mission-modal-item">
                                        <span className="mission-modal-key">최대 인정</span>
                                        <span className="mission-modal-value">
                                            {selectedMission.maxCount === 0
                                                ? '무제한'
                                                : `${selectedMission.maxCount.toLocaleString()}건`}
                                        </span>
                                    </div>
                                )}
                                <div className="mission-modal-item">
                                    <span className="mission-modal-key">시작일</span>
                                    <span className="mission-modal-value">{selectedMission.startDate}</span>
                                </div>
                                <div className="mission-modal-item">
                                    <span className="mission-modal-key">종료일</span>
                                    <span className="mission-modal-value">{selectedMission.endDate}</span>
                                </div>
                                <div className="mission-modal-item">
                                    <span className="mission-modal-key">대상</span>
                                    <span className="mission-modal-value">
                                        {selectedMission.targetType === 'ALL'
                                            ? '협력사 전체 라이더'
                                            : `특정 라이더 ${selectedMission.targetRiders.length}명`}
                                    </span>
                                </div>
                            </div>

                            {/* 설명 */}
                            {selectedMission.description && (
                                <div className="mission-modal-description">
                                    <span className="mission-mocal-key">설명</span>
                                    <p>{selectedMission.description}</p>
                                </div>
                            )}

                            {/* 대상 라이더 목록 (SPECIFIC인 경우) */}
                            {selectedMission.targetType === 'SPECIFIC' && (
                                <div className="mission-modal-riders">
                                    <div className="mission-modal-riders-title">
                                        대상 라이더
                                        <span className="mission-modal-riders-count">
                                            {selectedMission.targetRiders.length}명
                                        </span>
                                    </div>
                                    {selectedMission.targetRiders.length > 0 ? (
                                        <div className="mission-modal-rider-list">
                                            {selectedMission.targetRiders.map((id) => {
                                                const r = riders.find((x) => x.riderId === id);
                                                return (
                                                    <span key={id} className="mission-modal-rider-chip">
                                                        {r && (
                                                            <span className="mission-modal-rider-name">
                                                                {r.riderName}
                                                            </span>
                                                        )}
                                                        <span className="mission-modal-rider-id">{id}</span>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="mission-modal-no-riders">지정된 라이더가 없습니다.</p>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* 모달 하단 버튼 */}
                        {!isReadOnly && (
                            <div className="mission-modal-footer">
                                <button
                                    type="button"
                                    className="mission-btn mission-btn-primary"
                                    onClick={() => handleEditStart(selectedMission)}
                                >
                                    수정하기
                                </button>
                                {selectedMission.endDate >= today && (
                                    <button
                                        type="button"
                                        className={`mission-btn ${selectedMission.status === 'ACTIVE' ? 'mission-btn-warning' : 'mission-btn-activate'}`}
                                        onClick={() => handleStatusToggle(selectedMission)}
                                    >
                                        {selectedMission.status === 'ACTIVE' ? '비활성화' : '활성화'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="mission-btn mission-btn-danger"
                                    onClick={() => handleDelete(selectedMission.seqNo)}
                                >
                                    삭제
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PromotionSupplierToRiderPage;
